import type {
  AllocationScope,
  ConversationTurn,
  LessonPlanningData,
} from '../types/teacherAllocationAi'

/**
 * Persisted state for the AI teacher allocation assistant.
 *
 * Step4Manual unmounts whenever the user navigates to another wizard step, which would
 * otherwise drop the chat, the "Clear all" baseline and the undo stack (all held in
 * component state/refs). Persisting them lets the user step away and come back.
 *
 * sessionStorage, not localStorage, on purpose: logging out runs `localStorage.clear()`
 * in the user reducer, so a localStorage copy would be wiped. sessionStorage survives
 * both step navigation and logout -> login within the same tab.
 */
export type PersistedAllocationAiSession = {
  version: number
  conversationHistory: ConversationTurn[]
  baselineGrid: LessonPlanningData | null
  undoStack: LessonPlanningData[]
  applyCount: number
  scope: AllocationScope
}

const STORAGE_KEY_PREFIX = 'onboardingTeacherAllocationAi'
const SCHEMA_VERSION = 1

/**
 * Grid snapshots are full copies of the lesson planning grid (~40-300 KB each on a large
 * school), so an unbounded stack would blow the ~5 MB sessionStorage quota. Reverts
 * deeper than this are dropped oldest-first.
 */
export const MAX_UNDO_SNAPSHOTS = 10

/** Scope the key per user so a different login in the same tab never inherits a session. */
export function buildAllocationAiSessionKey(urddId?: string | null, tenant?: string | null) {
  return `${STORAGE_KEY_PREFIX}:${tenant || 'default'}:${urddId || 'anon'}`
}

function isConversationTurn(value: any): value is ConversationTurn {
  return (
    value &&
    typeof value === 'object' &&
    (value.role === 'user' || value.role === 'assistant') &&
    typeof value.content === 'string'
  )
}

function isGridLike(value: any): value is LessonPlanningData {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

export function readAllocationAiSession(key: string): PersistedAllocationAiSession | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    // Drop anything written by an older shape rather than half-restoring it.
    if (parsed.version !== SCHEMA_VERSION) return null

    const conversationHistory = Array.isArray(parsed.conversationHistory)
      ? parsed.conversationHistory.filter(isConversationTurn)
      : []
    const undoStack = Array.isArray(parsed.undoStack)
      ? parsed.undoStack.filter(isGridLike).slice(-MAX_UNDO_SNAPSHOTS)
      : []
    const baselineGrid = isGridLike(parsed.baselineGrid) ? parsed.baselineGrid : null
    const applyCount = Number.isFinite(parsed.applyCount) ? Math.max(0, parsed.applyCount) : 0

    // Without a baseline there is nothing to clear back to, so a non-zero applyCount
    // would leave "Clear all" enabled with nothing to restore.
    if (!baselineGrid && applyCount > 0) return null

    return {
      version: SCHEMA_VERSION,
      conversationHistory,
      baselineGrid,
      undoStack,
      applyCount,
      scope: parsed.scope === 'all_branches' ? 'all_branches' : 'active_filters',
    }
  } catch {
    // ignore storage / parse errors
    return null
  }
}

export function writeAllocationAiSession(
  key: string,
  session: Omit<PersistedAllocationAiSession, 'version'>
) {
  const undoStack = session.undoStack.slice(-MAX_UNDO_SNAPSHOTS)

  // Retry with a progressively shallower undo stack so a quota error costs undo depth
  // instead of losing the chat history and the "Clear all" baseline entirely.
  for (let keep = undoStack.length; keep >= 0; keep--) {
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({
          ...session,
          version: SCHEMA_VERSION,
          undoStack: undoStack.slice(undoStack.length - keep),
        })
      )
      return
    } catch {
      // quota exceeded (or storage unavailable) - shed a snapshot and try again
    }
  }
}

export function clearAllocationAiSession(key: string) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // ignore storage errors
  }
}
